var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
// ColoredTiles/LinearPiece/CoinsAndCounters/FractionTiles already dispatch
// multiSelectMoveStart/Move/End themselves (or build their own same-type "movables"/
// "initialSelectedPositions" list — CoinsAndCountersManipulative.tsx's onPointerDown/
// onPointerMove, FractionTileManipulative.tsx's onMouseDown/handleMove) and have their own
// bespoke same-type follower handling predating this engine. Since they use the same global
// event names, letting the central engine ALSO capture/move these types would double-apply
// the delta to any other selected item of the same type. Exclude them here until they're
// migrated off their own bespoke system onto this one.
var SELF_MANAGED_TYPES = new Set([
    'coloredTiles',
    'linearPiece',
    'coinsAndCounters',
    'fractionTiles',
    'linkingCubes',
]);
function clampToCanvas(x, y, _dimensions, _type, _canvasWidth, _canvasHeight) {
    // Clamping disabled for now (explicit request) — followers can move freely off-canvas
    // during a multi-select drag. Re-enable the [0, canvasWidth]/[0, canvasHeight] bound
    // below (a raw center/position clamp, no dimensions/2 offset — see prior comment history)
    // if bounding is needed again.
    return { x: x, y: y };
}
// Parent-child containment: skip a child whose parent is ALSO selected, since the parent's own
// drag logic already repositions its children (double-move otherwise).
var CONTAINMENT_SKIP_RULES = [
    { childType: 'numberFrameCounter', parentIdField: 'frameId' },
    { childType: '__group_member__', parentIdField: 'groupId', parentIdPrefix: 'manipulativeGroup_' },
];
function isSkippedByContainment(item, selectedTool) {
    var data = item.data;
    for (var _i = 0, CONTAINMENT_SKIP_RULES_1 = CONTAINMENT_SKIP_RULES; _i < CONTAINMENT_SKIP_RULES_1.length; _i++) {
        var rule = CONTAINMENT_SKIP_RULES_1[_i];
        if (rule.childType !== '__group_member__' && item.data.type !== rule.childType)
            continue;
        var parentId = data[rule.parentIdField];
        if (!parentId)
            continue;
        if (rule.parentIdPrefix && !String(parentId).startsWith(rule.parentIdPrefix))
            continue;
        if (rule.childType === '__group_member__' && !data.groupId)
            continue;
        if (selectedTool.includes(parentId))
            return true;
    }
    return false;
}
/**
 * Snapshots the canvas position of every item in `selectedTool` at drag start, excluding:
 *   - `excludeId` (the drag source — it moves itself via its own local drag logic)
 *   - locked items (`lock.positioning`) and outline items
 *   - items whose containing parent (frame/group) is also selected
 * Handles standard top-level manipulatives (`data.center`), dealt cards nested inside
 * `deck.data.cards[]` (`position`), and ManipulativeGroup members (`groupCenter`).
 */
export function captureMultiSelectStartPositions(manipulative, selectedTool, excludeId, extraExcludeIds) {
    var _a, _b, _c, _d, _e, _f;
    var result = [];
    var capturedIds = new Set();
    var skipReasons = {};
    if (excludeId)
        capturedIds.add(excludeId);
    extraExcludeIds === null || extraExcludeIds === void 0 ? void 0 : extraExcludeIds.forEach(function (id) { return capturedIds.add(id); });
    var _loop_1 = function (toolId) {
        if (!toolId.startsWith('manipulativeGroup_'))
            return "continue";
        // This exact group IS the drag leader (e.g. NumberAndMoneyPieceSelectionGroup's own
        // beginDrag) — its own leader code already moves every member directly via
        // setManipulative. Capturing them here too would double-apply the delta on top of
        // that (once via the leader's own move, once via this engine's commit), compounding
        // every frame into a runaway-stretched bounding box.
        if (toolId === excludeId)
            return "continue";
        var members = manipulative.filter(function (m) { return m.data.groupId === toolId; });
        for (var _k = 0, members_1 = members; _k < members_1.length; _k++) {
            var member = members_1[_k];
            var id = member.data.id;
            if (!id || capturedIds.has(id))
                continue;
            if ((_a = member.data.lock) === null || _a === void 0 ? void 0 : _a.positioning)
                continue;
            // ColoredTiles/LinearPiece groups also use the manipulativeGroup_ id prefix, but those
            // types own their own bespoke group-drag system (see SELF_MANAGED_TYPES above) — the
            // central engine must not also capture/commit their members directly, or a non-tile
            // leader (e.g. Dice) dragging alongside a selected tile-group would double-write them.
            if (SELF_MANAGED_TYPES.has((_b = member.data.type) !== null && _b !== void 0 ? _b : ''))
                continue;
            var groupCenter = member.data.groupCenter;
            var center = member.data.center;
            if (groupCenter) {
                result.push({ id: id, center: __assign({}, groupCenter), propertyName: 'groupCenter' });
            }
            else if (center) {
                result.push({ id: id, center: __assign({}, center), propertyName: 'center' });
            }
            else {
                continue;
            }
            capturedIds.add(id);
        }
    };
    // Pass 1: ManipulativeGroup entries — groupId in selectedTool → capture all members
    for (var _i = 0, selectedTool_1 = selectedTool; _i < selectedTool_1.length; _i++) {
        var toolId = selectedTool_1[_i];
        _loop_1(toolId);
    }
    // Pass 2: Standard top-level items — id directly in selectedTool
    for (var _g = 0, manipulative_1 = manipulative; _g < manipulative_1.length; _g++) {
        var item = manipulative_1[_g];
        var id = item.data.id;
        if (!id || capturedIds.has(id))
            continue;
        if (!selectedTool.includes(id))
            continue;
        if ((_c = item.data.lock) === null || _c === void 0 ? void 0 : _c.positioning) {
            skipReasons[id] = 'lock.positioning';
            continue;
        }
        if (item.data.isOutline || item.data.isStaticOutline) {
            skipReasons[id] = 'isOutline/isStaticOutline';
            continue;
        }
        if (isSkippedByContainment(item, selectedTool)) {
            skipReasons[id] = 'isSkippedByContainment';
            continue;
        }
        if (SELF_MANAGED_TYPES.has((_d = item.data.type) !== null && _d !== void 0 ? _d : '')) {
            skipReasons[id] = "SELF_MANAGED_TYPES (".concat(item.data.type, ")");
            continue;
        }
        var center = item.data.center;
        if (!center) {
            skipReasons[id] = 'no center property';
            continue;
        }
        result.push({ id: id, center: __assign({}, center), propertyName: 'center' });
        capturedIds.add(id);
    }
    // Pass 3: Dealt cards nested inside deck.data.cards[]
    for (var _h = 0, manipulative_2 = manipulative; _h < manipulative_2.length; _h++) {
        var item = manipulative_2[_h];
        if (item.data.type !== 'deck' && item.type !== 'cards')
            continue;
        var cards = (_e = item.data.cards) !== null && _e !== void 0 ? _e : [];
        for (var _j = 0, cards_1 = cards; _j < cards_1.length; _j++) {
            var card = cards_1[_j];
            if (!card.id || capturedIds.has(card.id))
                continue;
            if (!selectedTool.includes(card.id))
                continue;
            if ((_f = card.lock) === null || _f === void 0 ? void 0 : _f.positioning)
                continue;
            if (!card.position)
                continue;
            result.push({ id: card.id, center: __assign({}, card.position), propertyName: 'position' });
            capturedIds.add(card.id);
        }
    }
    var uncapturedIds = selectedTool.filter(function (id) { return id !== excludeId && !capturedIds.has(id); });
    if (uncapturedIds.length > 0) {
        // eslint-disable-next-line no-console
        console.log('[ResizeHolderDrag debug] captureMultiSelectStartPositions skipped ids', JSON.stringify({ excludeId: excludeId, extraExcludeIds: extraExcludeIds, uncapturedIds: uncapturedIds, skipReasons: skipReasons }));
    }
    return result;
}
/**
 * Converts start snapshots + final delta into savedDataInterface[] for a single batch undo entry.
 */
export function buildMultiSelectSaveEntries(startPositions, deltaX, deltaY) {
    return startPositions.map(function (_a) {
        var id = _a.id, center = _a.center, propertyName = _a.propertyName;
        return ({
            type: id,
            id: id,
            propertyName: propertyName,
            old: JSON.stringify(center),
            new: JSON.stringify({ x: center.x + deltaX, y: center.y + deltaY }),
        });
    });
}
/**
 * Returns a manipulative-array updater that commits final positions for ALL items
 * in `startPositions` based on a cumulative delta, clamped to canvas bounds using each
 * item's own anchor convention (most types are center-anchored; dice is top-left anchored).
 * Pass the result to `setManipulative(...)` directly.
 */
export function commitMultiSelectMove(startPositions, deltaX, deltaY, canvasWidth, canvasHeight) {
    var startMap = new Map(startPositions.map(function (p) { return [p.id, p]; }));
    return function (prev) {
        return prev.map(function (item) {
            try {
                return commitOneItem(item, startMap, deltaX, deltaY, canvasWidth, canvasHeight);
            }
            catch (err) {
                // eslint-disable-next-line no-console
                console.error('[commitMultiSelectMove debug] THREW while updating item — this item was' +
                    ' skipped this frame, but a throw here can silently stall the WHOLE batch if it happens' +
                    ' inside prev.map without a catch, since React discards the whole updater result on error', {
                    id: item.data.id,
                    type: item.data.type,
                    error: err,
                });
                return item;
            }
        });
    };
}
function commitOneItem(item, startMap, deltaX, deltaY, canvasWidth, canvasHeight) {
    var _a, _b;
    var nextItem = item;
    var start = startMap.get((_a = item.data.id) !== null && _a !== void 0 ? _a : '');
    if (start) {
        var raw = { x: start.center.x + deltaX, y: start.center.y + deltaY };
        var clamped = clampToCanvas(raw.x, raw.y, item.data.dimensions, item.data.type, canvasWidth, canvasHeight);
        if (clamped.x !== raw.x || clamped.y !== raw.y) {
            // eslint-disable-next-line no-console
            console.log('[commitMultiSelectMove debug] CLAMPED', {
                id: item.data.id,
                type: item.data.type,
                deltaX: deltaX,
                deltaY: deltaY,
                raw: raw,
                clamped: clamped,
                dimensions: item.data.dimensions,
                canvasWidth: canvasWidth,
                canvasHeight: canvasHeight,
            });
        }
        if (start.propertyName === 'groupCenter') {
            nextItem = __assign(__assign({}, nextItem), { data: __assign(__assign({}, nextItem.data), { groupCenter: clamped }) });
        }
        else {
            nextItem = __assign(__assign({}, nextItem), { data: __assign(__assign({}, nextItem.data), { center: clamped }) });
        }
    }
    // Dealt cards nested in deck.cards[] — short-circuit when no card in this deck was captured.
    if (nextItem.data.type === 'deck' || nextItem.type === 'cards') {
        var cards = (_b = nextItem.data.cards) !== null && _b !== void 0 ? _b : [];
        var anyCardCaptured = cards.some(function (c) { return startMap.has(c.id); });
        if (anyCardCaptured) {
            var newCards = cards.map(function (c) {
                var cardStart = startMap.get(c.id);
                if (!cardStart)
                    return c;
                return __assign(__assign({}, c), { position: { x: cardStart.center.x + deltaX, y: cardStart.center.y + deltaY } });
            });
            nextItem = __assign(__assign({}, nextItem), { data: __assign(__assign({}, nextItem.data), { cards: newCards }) });
        }
    }
    return nextItem;
}
/**
 * Text boxes live in their own `text: textInterface[]` array (not `manipulative`) and have no
 * stable `id` — they're addressed by array index via the synthetic selection-tool string
 * `text_<index>` (see GameController.tsx's `setSelectedTool(['text_' + text.length])` and the
 * same `.split('_')[1]` parsing used in helper.ts/ResizerComponent.tsx). Mirrors
 * captureMultiSelectStartPositions but keyed off that synthetic id and `data.position` instead
 * of `data.center`.
 */
export function captureTextStartPositions(text, selectedTool, excludeId, extraExcludeIds) {
    var _a;
    var result = [];
    var excluded = new Set();
    if (excludeId)
        excluded.add(excludeId);
    extraExcludeIds === null || extraExcludeIds === void 0 ? void 0 : extraExcludeIds.forEach(function (id) { return excluded.add(id); });
    for (var _i = 0, selectedTool_2 = selectedTool; _i < selectedTool_2.length; _i++) {
        var toolId = selectedTool_2[_i];
        if (!toolId.startsWith('text_'))
            continue;
        if (excluded.has(toolId))
            continue;
        var idx = Number(toolId.split('_')[1]);
        var item = text[idx];
        if (!item)
            continue;
        if ((_a = item.data.lock) === null || _a === void 0 ? void 0 : _a.positioning)
            continue;
        var position = item.data.position;
        if (!position)
            continue;
        result.push({ id: toolId, position: __assign({}, position) });
    }
    return result;
}
/**
 * Converts text start snapshots + final delta into savedDataInterface[] for the shared batch
 * undo entry (merged with the manipulative followers' entries so one Ctrl+Z reverts the whole
 * group move).
 */
export function buildTextSaveEntries(startPositions, deltaX, deltaY) {
    return startPositions.map(function (_a) {
        var id = _a.id, position = _a.position;
        return ({
            type: 'text',
            id: id.split('_')[1],
            propertyName: 'position',
            old: JSON.stringify(position),
            new: JSON.stringify({ x: position.x + deltaX, y: position.y + deltaY, z: position.z }),
        });
    });
}
/**
 * Returns a text-array updater that commits final positions for every captured text follower
 * based on a cumulative delta. Pass the result to `setText(...)` directly. No canvas clamping,
 * matching the current (disabled) clamping behavior for manipulative followers.
 */
export function commitTextMove(startPositions, deltaX, deltaY) {
    if (startPositions.length === 0)
        return function (prev) { return prev; };
    var startMap = new Map(startPositions.map(function (p) { return [p.id, p]; }));
    return function (prev) {
        return prev.map(function (item, idx) {
            var start = startMap.get("text_".concat(idx));
            if (!start)
                return item;
            return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { position: {
                        x: start.position.x + deltaX,
                        y: start.position.y + deltaY,
                        z: start.position.z,
                    } }) });
        });
    };
}
